// ShiftReport.jsx
import React, { useEffect, useState, useMemo } from 'react';
import { Box, Grid, Paper, TextField, MenuItem, Button, Typography, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import * as XLSX from 'xlsx';
dayjs.extend(isoWeek);

export default function ShiftReport({ sampleEmployees, sampleRosterLines, departments, shifts }) {
  const [filterDept, setFilterDept] = useState('');
  const [filterEmp, setFilterEmp] = useState('');
  const [viewMode, setViewMode] = useState('monthly'); // 'weekly' or 'monthly'
  const [selectedMonth, setSelectedMonth] = useState(dayjs().format('YYYY-MM'));
  const [weekStartDate, setWeekStartDate] = useState(dayjs().startOf('week').format('YYYY-MM-DD'));
  const [rows, setRows] = useState([]);
  const [violations, setViolations] = useState({});

  useEffect(() => { buildReport(); }, [filterDept, filterEmp, viewMode, selectedMonth, weekStartDate]);

  function filteredEmployees() {
    let list = sampleEmployees;
    if (filterDept) list = list.filter(e => String(e.deptId) === String(filterDept));
    if (filterEmp) list = list.filter(e => String(e.id) === String(filterEmp));
    return list;
  }

  function buildReport() {
    const emps = filteredEmployees();
    let reportRows = [];
    const newViolations = {};

    if (viewMode === 'monthly') {
      const [y, m] = selectedMonth.split('-').map(Number);
      const start = dayjs(`${y}-${String(m).padStart(2, '0')}-01`);
      const days = start.daysInMonth();
      emps.forEach(emp => {
        let weekoffCountByWeek = {};
        for (let d = 0; d < days; d++) {
          const date = start.add(d, 'day');
          const dateStr = date.format('YYYY-MM-DD');
          const line = sampleRosterLines.find(l => l.employeeId === emp.id && l.date === dateStr);
          const isWeekoff = line ? !!line.isWeekoff : false;
          const shiftId = line ? line.shiftId : null;

          reportRows.push({ id: `${emp.id}-${dateStr}`, employeeId: emp.id, employeeName: emp.name, date: dateStr, shiftId, shiftName: shifts.find(s=>s.id===shiftId)?.name || '', isWeekoff });

          const wk = date.isoWeek();
          weekoffCountByWeek[wk] = (weekoffCountByWeek[wk] || 0) + (isWeekoff ? 1 : 0);

          const duplicates = sampleRosterLines.filter(l => l.employeeId === emp.id && l.date === dateStr);
          if (duplicates.length > 1) {
            newViolations[emp.id] = newViolations[emp.id] || [];
            newViolations[emp.id].push({ type: 'overlap', date: dateStr, detail: 'Multiple assignments for same day' });
          }
        }

        for (const [wk, count] of Object.entries(weekoffCountByWeek)) {
          if (Number(count) !== 1) {
            newViolations[emp.id] = newViolations[emp.id] || [];
            newViolations[emp.id].push({ type: 'weekoff-count', week: wk, detail: `Week ${wk}: ${count} week-offs (should be 1)` });
          }
        }
      });
    }

    if (viewMode === 'weekly') {
      const start = dayjs(weekStartDate);
      const days = 7;
      emps.forEach(emp => {
        let weekoffCount = 0;
        for (let d = 0; d < days; d++) {
          const date = start.add(d, 'day');
          const dateStr = date.format('YYYY-MM-DD');
          const line = sampleRosterLines.find(l => l.employeeId === emp.id && l.date === dateStr);
          const isWeekoff = line ? !!line.isWeekoff : false;
          const shiftId = line ? line.shiftId : null;

          if (isWeekoff) weekoffCount++;

          const duplicates = sampleRosterLines.filter(l => l.employeeId === emp.id && l.date === dateStr);
          if (duplicates.length > 1) {
            newViolations[emp.id] = newViolations[emp.id] || [];
            newViolations[emp.id].push({ type: 'overlap', date: dateStr, detail: 'Multiple assignments for same day' });
          }

          reportRows.push({ id: `${emp.id}-${dateStr}`, employeeId: emp.id, employeeName: emp.name, date: dateStr, shiftId, shiftName: shifts.find(s=>s.id===shiftId)?.name || '', isWeekoff });
        }

        if (weekoffCount !== 1) {
          newViolations[emp.id] = newViolations[emp.id] || [];
          newViolations[emp.id].push({ type: 'weekoff-count', detail: `Week start ${start.format('YYYY-MM-DD')}: ${weekoffCount} week-offs (should be 1)` });
        }
      });
    }

    setRows(reportRows);
    setViolations(newViolations);
  }

  const columns = useMemo(() => [
    { field: 'date', headerName: 'Date', width: 120 },
    { field: 'employeeName', headerName: 'Employee', width: 220 },
    { field: 'shiftName', headerName: 'Shift', width: 260, renderCell: (p) => p.value || '-' },
    { field: 'isWeekoff', headerName: 'Week Off', width: 120, renderCell: (p) => p.value ? 'Yes' : 'No' },
    { field: 'violation', headerName: 'Violation', width: 320, renderCell: (p) => {
      const empViol = violations[p.row.employeeId] || [];
      const dateViol = empViol.filter(v => v.date === p.row.date || v.type === 'weekoff-count');
      if (dateViol.length === 0) return '-';
      return (<div style={{ color: 'red' }}>{dateViol.map((v,i)=> (<div key={i}>{v.detail}{v.week?` (week ${v.week})`:''}</div>))}</div>);
    } }
  ], [violations]);

  function exportExcel() {
    const wb = XLSX.utils.book_new();
    const wsData = rows.map(r => ({ Employee: r.employeeName, Date: r.date, Shift: r.shiftName, WeekOff: r.isWeekoff ? 'Yes' : '' }));
    const ws = XLSX.utils.json_to_sheet(wsData);
    XLSX.utils.book_append_sheet(wb, ws, 'Roster');
    XLSX.writeFile(wb, `roster-${viewMode}-${dayjs().format('YYYYMMDD')}.xlsx`);
  }

  function exportPDF() {
    alert('Export to PDF is a demo stub. Implement jsPDF or server-side PDF later.');
  }

  function sendToEmployee(empId) {
    alert(`Send schedule to employee ${empId} (stub).`);
  }

  return (
    <Box p={2}>
      <Typography variant='h6'>Shift Report — Weekly & Monthly</Typography>
      <Paper sx={{ p:2, mt:2 }}>
        <Grid container spacing={2} alignItems='center'>
          <Grid item>
            <TextField select label='Department' value={filterDept} onChange={e=>setFilterDept(e.target.value)} sx={{ minWidth:200 }}>
              <MenuItem value=''>-- All Departments --</MenuItem>
              {departments.map(d => <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item>
            <TextField select label='Employee' value={filterEmp} onChange={e=>setFilterEmp(e.target.value)} sx={{ minWidth:220 }}>
              <MenuItem value=''>-- All Employees --</MenuItem>
              {sampleEmployees.map(e => <MenuItem key={e.id} value={e.id}>{e.name}</MenuItem>)}
            </TextField>
          </Grid>

          <Grid item>
            <ToggleButtonGroup value={viewMode} exclusive onChange={(e, v) => v && setViewMode(v)}>
              <ToggleButton value='weekly'>Weekly</ToggleButton>
              <ToggleButton value='monthly'>Monthly</ToggleButton>
            </ToggleButtonGroup>
          </Grid>

          {viewMode === 'monthly' && (
            <Grid item>
              <TextField label='Month' type='month' value={selectedMonth} onChange={e=>setSelectedMonth(e.target.value)} InputLabelProps={{ shrink: true }} />
            </Grid>
          )}

          {viewMode === 'weekly' && (
            <Grid item>
              <TextField label='Week Start' type='date' value={weekStartDate} onChange={e=>setWeekStartDate(e.target.value)} InputLabelProps={{ shrink: true }} />
            </Grid>
          )}

          <Grid item>
            <Button variant='contained' onClick={buildReport}>Refresh Report</Button>
          </Grid>

          <Grid item>
            <Button onClick={exportExcel}>Export Excel</Button>
          </Grid>

          <Grid item>
            <Button onClick={exportPDF}>Export PDF</Button>
          </Grid>

        </Grid>
      </Paper>

      <Box mt={2} style={{ height: 520 }}>
        <DataGrid rows={rows} columns={columns} pageSize={30} rowsPerPageOptions={[30, 60]} getRowId={r => r.id} />
      </Box>

      <Box mt={2}>
        <Typography variant='subtitle1'>Violations summary:</Typography>
        {Object.keys(violations).length === 0 ? <div>No violations</div> : (
          <div>
            {Object.entries(violations).map(([empId, vs]) => (
              <Paper key={empId} sx={{ p:1, mb:1 }}>
                <b>{sampleEmployees.find(e=>e.id===Number(empId))?.name || empId}</b>
                <ul>
                  {vs.map((v,i)=> <li key={i} style={{ color: 'red' }}>{v.detail}{v.week?` (week ${v.week})`:''}</li>)}
                </ul>
                <Button size='small' onClick={()=>sendToEmployee(empId)}>Send to employee</Button>
              </Paper>
            ))}
          </div>
        )}
      </Box>
    </Box>
  );
}
